<template>
  <SoRadioCards
    v-model="workstreams"
    class="mt-10 mb-14"
    :mandatory="false"
    :multiple="true"
  >
    <v-row>
      <v-col
        v-for="filter in topFilters"
        :key="filter.name"
        cols="12"
        sm="6"
        lg="3"
      >
        <SoRadioCardChart
          :title="filter.label"
          :value="filter.name"
          :percentage="filter.percentage"
          :type="filter.type"
          :text="`${filter.count} - ${filter.percentage}%`"
          :is-loading="smartMetersSummary.isFetching"
        />
      </v-col>
    </v-row>
  </SoRadioCards>

  <SoAlert v-if="query?.error" title="Failed to render smart meters table" />

  <SoDataTableServer
    v-else
    v-model="selectedMeters"
    v-model:options="options"
    :headers="headers"
    :data="tableData"
    :are-filters-loading="smartMeterColumnValues.isFetching"
    :selectable="allowAction"
    search-placeholder="e.g. 2000001234567"
    select-key="pk"
    filter-width="440px"
    searchable
    card
    data-testid="admin-smart-meters-table"
    @fetch="fetchSmartMeters"
  >
    <template v-if="allSelected" #prependBody="{ totalItems }">
      <div class="rounded bg-border my-2 py-3 text-center font-weight-regular">
        <template v-if="downloadSmartMeters === DownloadSmartMetersBy.ID">
          <span class="font-weight-medium">{{ selectedMeters.length }}</span>
          Smart Meters are selected.
          <a
            v-if="tableData.totalItems !== totalSelectedItems"
            class="text-primary text-decoration-none font-weight-medium"
            href="javascript:void(0);"
            @click="downloadSmartMeters = DownloadSmartMetersBy.FILTER"
          >
            Select All {{ totalItems }} Smart Meters under the current filters?
          </a>
        </template>
        <template
          v-else-if="downloadSmartMeters === DownloadSmartMetersBy.FILTER"
        >
          All <span class="font-weight-medium">{{ totalItems }}</span> Smart
          Meters are selected.
          <a
            class="text-primary text-decoration-none font-weight-medium"
            href="javascript:void(0);"
            @click="downloadSmartMeters = DownloadSmartMetersBy.ID"
          >
            Undo?
          </a>
        </template>
      </div>
    </template>
    <template #tableActions>
      <SoButton
        v-if="allowAction"
        data-testid="table-action"
        :disabled="!selectedMeters.length"
        :title="
          'Actions ' + (totalSelectedItems ? ` (${totalSelectedItems})` : '')
        "
        large
        type="primary"
        @click="showDialog"
      ></SoButton>
      <SoButton
        :disabled="isDownloading || tableData.totalItems === 0"
        :loading="isDownloading"
        title="Download"
        prepend-icon="download"
        large
        @click="download"
      />
      <v-spacer />
      <SoSkeleton v-if="syncedDateTime.isFetching" width="12em" height="2em" />
      <div v-else class="font-weight-medium">
        Data last updated: <span>{{ lastSyncedDateTime }}</span>
      </div>
    </template>
    <template #header.ssd="{ column }">
      <SoTooltip
        text="Supply Start Date for the premise where this meter is located"
        >{{ column.title }}</SoTooltip
      >
    </template>
    <template #header.readingDateTime="{ column }">
      <SoTooltip
        text="The last time that a meter sent a reading via the Data Communications Company according to ESG Centre Stage"
        >{{ column.title }}</SoTooltip
      >
    </template>
    <template #item.mpxn="{ internalItem }">
      <div class="d-flex align-center text-no-wrap">
        <SoIcon
          v-if="internalItem.raw.meterType"
          :icon="internalItem.raw.isGas ? 'gas' : 'electric'"
          :color="internalItem.raw.isGas ? 'gas' : 'electric'"
          size="small"
          class="mr-1 mb-1"
        />
        <span>{{ internalItem.raw.mpxn }} </span>
      </div>
    </template>
    <template #item.deviceId="{ internalItem }">
      <span class="text-no-wrap">{{ internalItem.columns.deviceId }}</span>
    </template>
    <template #item.errorCode="{ internalItem }">
      <SoTooltip
        :text="errorCodes[internalItem.columns.errorCode]"
        :disabled="!errorCodes[internalItem.columns.errorCode]"
      >
        {{ internalItem.columns.errorCode }}
      </SoTooltip>
    </template>
    <template #item.latestReading="{ internalItem }">
      {{ internalItem.columns.latestReading }}
    </template>
    <template #item.latestAlert="{ internalItem }">
      <SoTooltip
        :text="alertCodes[internalItem.columns.latestAlert]"
        :disabled="!alertCodes[internalItem.columns.latestAlert]"
      >
        {{ internalItem.columns.latestAlert }}
      </SoTooltip>
    </template>
    <template #item.commsHubId="{ internalItem }">
      <div class="comms-hub-id">
        {{ internalItem.columns.commsHubId }}
      </div>
    </template>
    <template #item.modelName="{ internalItem }">
      <p
        v-for="text in internalItem.columns.modelName"
        :key="text"
        class="text-no-wrap"
      >
        {{ text }}
      </p>
    </template>
    <template #item.manufacturerName="{ internalItem }">
      <p
        v-for="text in internalItem.columns.manufacturerName"
        :key="text"
        class="text-no-wrap"
      >
        {{ text }}
      </p>
    </template>
  </SoDataTableServer>
</template>

<script lang="ts">
  import { defineComponent } from "vue"
  import { errorCodes, alertCodes } from "@/constants/smartMeters"
  import { typeformIds } from "@/constants/typeformIds"
  import { safeDayjs } from "@/helpers/dateHelper"
  import { QueryResult } from "@/api/utils/types"
  import {
    SmartMeterService,
    SmartMeterQueryResult,
  } from "@/services/operations/SmartMeterService"
  import TypeformService from "@/services/TypeformService"
  import { errorHandler, errorReporter } from "@/services/ErrorService"
  import { SmartMeterMutator } from "@/mutators/smartMeter/SmartMeterMutator"
  import { useSnackbarStore } from "@/stores/snackbar"
  import SoAlert from "@/components/SoAlert.vue"
  import SoButton from "@/components/formControls/SoButton.vue"
  import SoDataTableServer, {
    SoDataTableData,
    SoDataTableOptions,
  } from "@/components/dataTable/SoDataTableServer.vue"
  import SoIcon from "@/components/SoIcon.vue"
  import SoRadioCardChart from "@/components/SoRadioCardChart.vue"
  import SoRadioCards from "@/components/SoRadioCards.vue"
  import SoSkeleton from "@/components/SoSkeleton.vue"
  import SoTooltip from "@/components/SoTooltip.vue"
  import { DownloadSmartMetersBy } from "@/models/smartMeter/smartMeter.types"
  import { IStatus } from "@/types/Status"
  import { GetSmartMetersDataFileUrlResponse } from "@/api/backofficeRest/generated/model"
  import {
    TableFilters,
    TopFilter,
    SmartMeterFilterTypes,
    ReadingsReceived,
    topFilters,
    headers,
    tableFilters,
  } from "../opsSmartMeterConfig"
  import dayjs from "dayjs"
  import { AuthService } from "@/services/AuthService"
  import { ROLES } from "@/services/PermissionsService"

  const MAX_DOWNLOAD_LIMIT = 2_000_000

  export default defineComponent({
    components: {
      SoDataTableServer,
      SoIcon,
      SoTooltip,
      SoRadioCards,
      SoRadioCardChart,
      SoSkeleton,
      SoAlert,
      SoButton,
    },
    data() {
      return {
        query: null as QueryResult<SmartMeterQueryResult> | null,
        selectedMeters: [] as number[],
        isDownloading: false,
        headers,
        errorCodes,
        alertCodes,
        options: {
          filters: tableFilters,
        } as Partial<SoDataTableOptions>,
        downloadSmartMeters: DownloadSmartMetersBy.ID,
        DownloadSmartMetersBy,
        snackbarStore: useSnackbarStore(),
        smartMetersSummary: SmartMeterService.getSmartMetersSummary(),
        syncedDateTime: SmartMeterService.getLastSyncedDateTime(),
        smartMeterColumnValues: SmartMeterService.getUniqueColumnValues(),
      }
    },
    computed: {
      tableData(): SoDataTableData {
        return {
          isLoading: !!this.query?.isFetching,
          totalItems: this.query?.data?.totalCount ?? 0,
          items:
            this.query?.data?.records?.map((record) => ({
              ...record,
              isGas: record.isGas,
              isElectricity: record.isElectricity,
              latestReading: record.latestReading,
              workstreamStatuses: record.workstreamStatuses.map(
                (status: IStatus) => ({
                  props: {
                    status,
                  },
                }),
              ),
              modelName: record.modelName,
              manufacturerName: record.manufacturerName,
            })) ?? [],
        }
      },
      topFilters(): TopFilter[] {
        const total = this.smartMetersSummary?.data?.total ?? 0
        return topFilters.map((filter) => {
          const count = this.smartMetersSummary?.data?.[filter.name] ?? 0
          const percentage = count === 0 ? 0 : Math.round((count / total) * 100)
          return {
            ...filter,
            count,
            percentage,
          }
        })
      },
      workstreams: {
        get(): string[] | null {
          if (this.options.filters?.workstream) {
            return Array.isArray(this.options.filters.workstream.value)
              ? (this.options.filters.workstream.value as string[])
              : []
          }
          return null
        },
        set(value: string[]) {
          if (this.options.filters) {
            this.options.filters.workstream.value = value
          }
        },
      },
      allSelected() {
        return (
          this.tableData.items.length &&
          this.selectedMeters.length &&
          this.tableData.items.every((meter) =>
            this.selectedMeters.includes(meter.pk as number),
          )
        )
      },
      totalSelectedItems() {
        return this.downloadSmartMeters === DownloadSmartMetersBy.ID
          ? this.selectedMeters.length
          : this.tableData.totalItems
      },
      lastSyncedDateTime() {
        return this.syncedDateTime?.data
          ? dayjs(this.syncedDateTime?.data).format("DD MMM YYYY HH:mm")
          : "N/A"
      },
      smartMeterColumnFilters() {
        return this.smartMeterColumnValues.data
      },
      allowAction() {
        const roles = AuthService.getUserRoles()
        return roles.includes(ROLES.METERING) || AuthService.isDeveloper()
      },
    },
    watch: {
      allSelected: {
        handler(val) {
          if (!val) {
            this.downloadSmartMeters = DownloadSmartMetersBy.ID
          }
        },
      },
      smartMeterColumnFilters: {
        handler(val) {
          this.updateColumnFilters(val)
        },
      },
    },
    methods: {
      updateColumnFilters(data) {
        if (!data) return
        Object.keys(data).forEach((filterName) => {
          const filter = this.options?.filters?.[filterName]
          if (filter && "options" in filter) {
            filter.options = data[filterName]
          }
        })
      },
      fetchSmartMeters({
        search,
        page,
        pageSize,
        orderBy,
        filters,
      }: SoDataTableOptions) {
        if (orderBy && Object.keys(orderBy).length === 0 && !this.query) {
          orderBy["installation_date"] = "desc"
        }

        this.query = SmartMeterService.getSmartMeterList(
          search,
          page,
          pageSize,
          orderBy,
          this.mapFilters(filters as TableFilters),
        )
      },
      mapFilters(filters: TableFilters): SmartMeterFilterTypes {
        const mapArrayFilter = (filterValue: string[]) =>
          filterValue?.length ? filterValue : undefined

        const mapDateToFilter = (date: Date | null | undefined) =>
          date ? safeDayjs(date)?.add(1, "day") : undefined

        return {
          alerts: mapArrayFilter(filters.latestAlert.value),
          alertDateFrom: safeDayjs(filters.latestAlertDate.value.from),
          alertDateTo: mapDateToFilter(filters.latestAlertDate.value.to),
          alertStatuses: mapArrayFilter(filters.latestAlertStatus.value),
          deviceStatuses: mapArrayFilter(filters.deviceStatus.value),
          errorCodes: mapArrayFilter(filters.errorCode.value),
          errorStatuses: mapArrayFilter(filters.errorStatus.value),
          firmwareStatuses: mapArrayFilter(filters.firmwareStatus.value),
          firmwareVersions: mapArrayFilter(filters.firmwareVersion.value),
          gbcsVersions: mapArrayFilter(filters.gbcsVersion.value),
          installedDateFrom: safeDayjs(filters.installedDate.value.from),
          installedDateTo: mapDateToFilter(filters.installedDate.value.to),
          lastOrchestrations: mapArrayFilter(filters.lastOrchestration.value),
          lastReadDateFrom: safeDayjs(filters.lastReadDate.value.from),
          lastReadDateTo: mapDateToFilter(filters.lastReadDate.value.to),
          manufacturers: mapArrayFilter(filters.manufacturer.value),
          meterType: mapArrayFilter(filters.meterType.value),
          models: mapArrayFilter(filters.model.value),
          orchestrationStatuses: mapArrayFilter(
            filters.orchestrationStatus.value,
          ),
          paymentModes: mapArrayFilter(filters.paymentMode.value),
          readingsReceived: this.readingsReceivedFilterValue(
            filters.readingsReceived.value,
          ),
          regions: mapArrayFilter(filters.region.value),
          serviceRequests: mapArrayFilter(filters.serviceRequest.value),
          smetsVersions: mapArrayFilter(filters.smetsVersion.value),
          variants: mapArrayFilter(filters.variant.value),
          ssdFrom: safeDayjs(filters.ssd.value.from),
          ssdTo: mapDateToFilter(filters.ssd.value.to),
          workstreams: mapArrayFilter(filters.workstream.value),
        }
      },
      readingsReceivedFilterValue(value: keyof typeof ReadingsReceived | null) {
        switch (value) {
          case ReadingsReceived.Received:
            return true
          case ReadingsReceived.Never:
            return false
          default:
            return undefined
        }
      },
      async showDialog() {
        await TypeformService.openPopup(typeformIds.SMART_METERS_ACTION, {
          height: 500,
          width: 700,
        })
      },
      async download() {
        this.isDownloading = true

        try {
          const filters = this.mapFilters(this.options.filters as TableFilters)
          const orderBy = this.options.orderBy || {}
          const sortColumn = Object.keys(orderBy)?.[0] ?? ""
          const sortDirection = orderBy[sortColumn] ?? ""
          const data = (await SmartMeterMutator.download().mutateAsync({
            limit: MAX_DOWNLOAD_LIMIT,
            offset: 0,
            sortColumn,
            sortDirection,
            ...filters,
          })) as GetSmartMetersDataFileUrlResponse
          const fileUrl = SmartMeterService.extractFileUrl(data)

          if (fileUrl) {
            window.open(fileUrl)
          } else {
            throw new Error("No valid file URL returned.")
          }
        } catch (error) {
          this.snackbarStore.addError(
            errorHandler.userMessage(
              error,
              "Failed to download smart meter data",
            ),
          )
          errorReporter.report(error)
        } finally {
          this.isDownloading = false
        }
      },
    },
  })
</script>
<style scoped>
  .comms-hub-id {
    min-width: 200px;
  }
</style>
